import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { BEZ_TOWARU, STOCK_STEPS, stanSzukania, stockStep } from '@/lib/planogram-kolory';
import type { Compartment, Planogram as PlanogramData } from '@/lib/planogram-api';
import { cn } from '@/lib/utils';

/**
 * BĘBEN W 3D — pokazuje to, czego siatka pokazać nie umie: że kolumny stoją na OBWODZIE.
 *
 * Siatka jest rozwinięciem bębna na płasko i to jest dobre do czytania numerów, ale przy 54
 * kolumnach traci sens fizyczny: kolumna 54 sąsiaduje z kolumną 1, a na płaskiej siatce dzieli
 * je cały ekran. Serwisant otwierający automat widzi walec i tak go obraca.
 *
 * ⚠️ BEZ NOWEJ ZALEŻNOŚCI. To są transformacje CSS: kolumna jedzie `rotateY(kąt) translateZ(promień)`,
 * a całość obraca się jednym `rotateY` na kontenerze. Biblioteka 3D dołożyłaby do klienta kilkaset
 * kilobajtów po to, żeby narysować walec z prostokątów.
 */

/**
 * Szerokość JEDNEGO miejsca bębna i wysokość wiersza w pikselach.
 *
 * ⚠️ Podniesione po zgłoszeniu „teraz wszystko jest zbyt zbite". Walec ma tu być czytelny, a nie
 * zmieścić się w jak najmniejszym kadrze — przy 54 kolumnach i tak widać naraz najwyżej połowę.
 */
const MIEJSCE_PX = 40;
/** Szczelina MIĘDZY kolumnami; zgłoszone jako za mały odstęp. */
const SZCZELINA_PX = 6;
const WIERSZ_PX = 28;
/** O ile zaznaczona przegródka wysuwa się z bębna — patrz komentarz przy `wysunieta`. */
const WYSUNIECIE_PX = 16;

/**
 * ⚠️ BĘBEN NIE JEST POCHYLONY I TO JEST WYMÓG, NIE PRZEOCZENIE.
 *
 * Wcześniej stało tu `rotateX(12deg)` „jak przy otwartych drzwiach" — i zgłoszenie brzmiało:
 * „bardzo zła perspektywa, wiersz środkowy musi być na wprost". Pochylenie odchyla środek bębna
 * od patrzącego, więc żaden wiersz nie jest widziany prosto, a górne komórki uciekają w skrót
 * perspektywiczny. Kamera patrzy więc na wysokości środka i pod zero stopni.
 */
const POCHYLENIE_ST = 0;

/**
 * OGNISKOWA JAKO KROTNOŚĆ PROMIENIA — im mniejsza, tym silniejsza perspektywa.
 *
 * Było `× 4`, czyli ogniskowa tak długa, że walec wychodził płaski („mimo że się obraca, wydaje
 * się dość płaski").
 *
 * ⚠️ TA LICZBA STERUJE TEŻ WYSOKOŚCIĄ KADRU, więc nie da się jej dobrać patrząc tylko na głębię:
 * przednia ściana rzutuje się powiększona o `x / (x − 1)`, czyli przy `× 2` DWUKROTNIE. Przy
 * trzynastu wierszach po 28 px dawało to kadr 800 px, którego nie widać naraz na ekranie.
 * `× 2.5` to powiększenie 1,67× — głębia zostaje wyraźna, a walec mieści się w oknie.
 * Bez uwzględnienia tego mnożnika przednie kolumny wychodzą poza kadr razem z numerami
 * (zgłoszone: „górny wiersz jest zupełnie ucięty, nie widać numerków").
 */
const OGNISKOWA_X_PROMIEN = 2.5;

/**
 * ⚠️ TEN SAM KUBEŁEK CO W SIATCE. Bęben miał wcześniej własne trzy kolory i przegródka pusta
 * wychodziła w nim ŻÓŁTA, a w siatce CZERWONA — zgłoszone wprost. Kolor jest znaczeniem, więc
 * dwa znaczenia tej samej rzeczy na jednym ekranie to błąd. Paleta jest teraz jedna
 * (`planogram-kolory.ts`) i oba widoki liczą z niej dokładnie tak samo.
 */
function kolorPrzegrodki(c: Compartment | null): { bg: string; ink: string; obrys: string | null } | null {
  if (c === null) return null;
  // brak PRZYPISANEGO towaru to informacja o planogramie, nie o zapasie — sam obrys
  if (c.item === null) return { bg: BEZ_TOWARU.bg, ink: BEZ_TOWARU.ink, obrys: BEZ_TOWARU.border };
  const s = stockStep(c.fillPercent ?? 0);
  return { bg: s.bg, ink: s.ink, obrys: null };
}

export function Beben3D({
  drawers,
  rows,
  zaznaczone,
  szukany,
  onToggle,
}: {
  /** kolumny JEDNEGO bębna — bęben z dostawki rysuje się osobno, bo to osobny walec */
  drawers: PlanogramData['drawers'];
  rows: number;
  zaznaczone: ReadonlySet<string>;
  /**
   * Szukany towar — PODŚWIETLENIE, nie zaznaczenie. Ten sam kanał co w siatce, żeby przełączenie
   * widoku nie zmieniało znaczenia tego, co widać.
   */
  szukany: string | null;
  onToggle: (c: Compartment, mod: { shift: boolean; toggle: boolean }) => void;
}) {
  const { t } = useI18n();
  const [kat, setKat] = useState(0);
  /**
   * ETYKIETY NA BĘBNIE — nazwa towaru wpisana w przegródkę.
   *
   * ⚠️ DOMYŚLNIE WŁĄCZONE — ZMIENIONE NA PROŚBĘ UŻYTKOWNIKA. Wcześniej startowały wyłączone,
   * bo przy 54 kolumnach napis mieści się w komórce dopiero po przycięciu do kilku znaków,
   * a przycięta nazwa bywa myląca („REKAWICE…" pasuje do dwudziestu pozycji). W praktyce jednak
   * pierwsze pytanie do bębna brzmi „co tu leży", a nie „jak rozłożony jest zapas" — więc lepiej
   * zacząć od nazw i pozwolić je schować, niż zaczynać od kolorów i kazać nazwy włączać.
   * Przełącznik zostaje na te chwile, gdy szuka się
   * konkretnego towaru wzrokiem po obwodzie.
   */
  const [etykiety, setEtykiety] = useState(true);
  const przeciaganie = useRef<{ x: number; kat: number; ruszone: boolean } | null>(null);

  /**
   * ⚠️ LICZBA MIEJSC Z DANYCH, NIE ZE STAŁEJ. Bęben ma tyle pozycji, ile zajmuje ostatnia
   * kolumna wraz ze swoim poszerzeniem — a to jest geometria konkretnej maszyny konkretnego
   * klienta. Zakodowane 54 dałoby walec wyglądający poprawnie i rozjeżdżający się z automatem.
   */
  const miejsc = useMemo(
    () => Math.max(1, ...drawers.map((d) => d.drawerNo + Math.max(1, d.span) - 1)),
    [drawers],
  );

  /**
   * KĄT SPOCZYNKOWY — KOLUMNA 1 NA WPROST, nie „zero stopni".
   *
   * ⚠️ Zero wypadało na SZCZELINIE między ostatnią kolumną a pierwszą, bo środek kolumny 1 leży
   * o pół miejsca dalej niż początek obwodu (przy poszerzonej — o połowę jej szerokości).
   * Zgłoszone wprost: „teraz jest pomiędzy 54 a 1". Przesunięcie liczymy z geometrii TEGO bębna,
   * zamiast wpisywać stałą, która rozjechałaby się przy innym rozmiarze.
   */
  const katKolumny1 = useMemo(() => {
    const pierwsza = drawers[0];
    if (pierwsza === undefined || miejsc === 0) return 0;
    return -((Math.max(1, pierwsza.span) / 2 / miejsc) * 360);
  }, [drawers, miejsc]);

  /** Zmiana automatu albo bębna zmienia geometrię, więc pozycja spoczynkowa przelicza się sama. */
  useEffect(() => setKat(katKolumny1), [katKolumny1]);

  /** Obwód = miejsc × szerokość miejsca, więc promień wychodzi wprost — kolumny stykają się bokami. */
  const promien = Math.round((miejsc * MIEJSCE_PX) / (2 * Math.PI));
  const wysokosc = rows * WIERSZ_PX;

  /**
   * WYSOKOŚĆ KADRU LICZONA Z POWIĘKSZENIA, nie dobrana na oko.
   *
   * Element na `translateZ(+r)` jest bliżej oka, więc rzutuje się większy w stosunku
   * `ogniskowa / (ogniskowa − r)`. Przy ogniskowej `2r` to dokładnie dwa razy. Kadr musi pomieścić
   * powiększoną kolumnę RAZEM z paskiem numeru nad nią — poprzednia wersja rezerwowała stałe
   * `+120 px` i przy tej samej geometrii ucinała górę.
   */
  const powiekszenie = OGNISKOWA_X_PROMIEN / (OGNISKOWA_X_PROMIEN - 1);
  const wysokoscKadru = Math.round(wysokosc * powiekszenie) + 72;

  /**
   * ⚠️ PRZECHWYTUJEMY WSKAŹNIK DOPIERO PO RUCHU — i to jest poprawka zgłoszonego „w widoku 3d
   * nie mogę zaznaczyć żadnej kratki".
   *
   * Wcześniej `setPointerCapture` szło już na `pointerdown`. Przechwycony wskaźnik należy do
   * kontenera, więc `click` nigdy nie docierał do przycisku przegródki — obrót zjadał każde
   * kliknięcie. Teraz kontener przejmuje wskaźnik dopiero, gdy myszka faktycznie przejedzie
   * kilka pikseli; poniżej progu zdarzenie leci dalej i komórka daje się zaznaczyć.
   */
  const PROG_PRZECIAGNIECIA_PX = 4;

  const zacznij = useCallback(
    (e: React.PointerEvent) => {
      przeciaganie.current = { x: e.clientX, kat, ruszone: false };
    },
    [kat],
  );

  const ciagnij = useCallback((e: React.PointerEvent) => {
    const p = przeciaganie.current;
    if (p === null) return;
    const dx = e.clientX - p.x;
    if (!p.ruszone) {
      if (Math.abs(dx) < PROG_PRZECIAGNIECIA_PX) return;
      p.ruszone = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    // 2 px myszy na stopień — przy pełnym obrocie 720 px daje ruch, który da się kontrolować
    setKat(p.kat + dx / 2);
  }, []);

  const skoncz = useCallback((e: React.PointerEvent) => {
    if (przeciaganie.current?.ruszone === true) e.currentTarget.releasePointerCapture(e.pointerId);
    przeciaganie.current = null;
  }, []);

  const wiersze = Array.from({ length: rows }, (_, i) => i + 1);

  return (
    <div className="space-y-2">
      <div
        /**
         * ⚠️ `touch-none`, bo bez tego przeciągnięcie po bębnie na tablecie przewija stronę
         * zamiast obracać walec — a to jest jedyny sposób obrotu, jaki tam jest.
         */
        className="relative touch-none select-none overflow-hidden rounded-lg border border-border bg-muted/20"
        style={{ height: wysokoscKadru, perspective: `${promien * OGNISKOWA_X_PROMIEN}px` }}
        onPointerDown={zacznij}
        onPointerMove={ciagnij}
        onPointerUp={skoncz}
        onPointerCancel={skoncz}
      >
        {/*
          ⚠️ STEROWANIE WEWNĄTRZ RAMKI I Z ZATRZYMANIEM ZDARZEŃ WSKAŹNIKA. Ramka nasłuchuje
          `pointerdown`, żeby obracać bęben przeciągnięciem — bez `stopPropagation` chwycenie
          suwaka albo kliknięcie przełącznika zaczynałoby jednocześnie obrót i walec skakałby
          pod palcem. Na dole zostaje wtedy sama legenda.
        */}
        <div
          className="absolute left-3 top-3 z-10"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-pressed={etykiety}
            onClick={() => setEtykiety((v) => !v)}
            /**
             * Zaznaczony na zielono, niezaznaczony na biało — wprost jak poproszono. Zieleń bierzemy
             * z tej samej zmiennej co przegródka „w normie", więc przełącznik nie wprowadza
             * czwartego zielonego do ekranu, który ma zarezerwowaną paletę stanów.
             */
            className={cn(
              'rounded-md border px-2.5 py-1 text-[11px] font-medium shadow-sm transition-colors',
              etykiety ? 'border-transparent' : 'border-border bg-white text-neutral-900 hover:bg-neutral-100',
            )}
            style={etykiety ? { background: 'var(--pg-good)', color: 'var(--pg-good-ink)' } : undefined}
          >
            {t('planogram.d3.labels')}
          </button>
        </div>

        <div
          className="absolute right-3 top-3 z-10 flex items-center gap-2 rounded-md border border-border bg-card/90 px-2 py-1 shadow-sm backdrop-blur"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
        >
          {/* Sama ikona zawiniętej strzałki — „wyprostuj" mieści się w niej bez podpisu. */}
          <Button
            variant="ghost"
            size="sm"
            className="size-7 p-0"
            aria-label={t('planogram.d3.reset')}
            title={t('planogram.d3.reset')}
            onClick={() => setKat(katKolumny1)}
          >
            <RotateCcw />
          </Button>
          <input
            type="range"
            min={-180}
            max={180}
            step={1}
            /* Suwak pokazuje obrót WZGLĘDEM pozycji spoczynkowej — środek skali to kolumna 1. */
            value={((((kat - katKolumny1 + 180) % 360) + 360) % 360) - 180}
            onChange={(e) => setKat(Number(e.target.value) + katKolumny1)}
            aria-label={t('planogram.d3.rotate')}
            className="h-1 w-40 accent-primary"
          />
        </div>

        <div
          className="absolute left-1/2 top-1/2"
          style={{
            transformStyle: 'preserve-3d',
            transform: `translate(-50%, -50%) rotateX(${POCHYLENIE_ST}deg) rotateY(${kat}deg)`,
            width: MIEJSCE_PX,
            height: wysokosc,
          }}
        >
          {drawers.map((k) => {
            const span = Math.max(1, k.span);
            /** Środek kolumny na obwodzie — kolumna poszerzona siedzi na dwóch/trzech miejscach. */
            const srodek = k.drawerNo - 1 + span / 2;
            const obrot = (srodek / miejsc) * 360;
            const poWierszach = new Map(k.compartments.map((c) => [c.rowNo, c]));

            /**
             * CIENIOWANIE OD KĄTA — najtańsza rzecz, która sprawia, że walec przestaje wyglądać
             * płasko. Same transformacje CSS dają geometrię, ale wszystkie ściany świecą tak samo
             * jasno, więc oko czyta to jako pofalowaną płachtę, a nie bryłę (zgłoszone wprost).
             *
             * Kolumna zwrócona do patrzącego ma `cos = 1`, boczna `cos = 0`. Przyciemnienie schodzi
             * więc od zera do 45 % — dalej wszystko jest czytelne, a krawędź bębna wyraźnie ucieka.
             *
             * ⚠️⚠️ CIEŃ JEST NAKŁADKĄ, NIE `filter: brightness()`, i to jest cała poprawka
             * rozmycia zgłoszonego 14.08.2026. Filtr CSS wymusza narysowanie elementu do OSOBNEJ
             * TEKSTURY, a dopiero potem rzutowanie jej perspektywą — a przednia ściana walca jest
             * przy naszej ogniskowej powiększana 1,67×. Napis rysowany w rozdzielczości 1× i
             * rozciągany do 167 % to dokładnie ta miękka, zamazana litera, którą widać było na
             * etykietach. Bez filtru przeglądarka rysuje tekst PO rzutowaniu, czyli w docelowej
             * wielkości i ostro.
             *
             * ⚠️ FILTR SPŁASZCZAŁ TEŻ PRZESTRZEŃ 3D. Element z `filter` wymusza `transform-style:
             * flat` na potomkach, więc `translateZ` na zaznaczonej przegródce nie miał w czym się
             * wysunąć — zostawało samo `scale`. Po usunięciu filtru wysunięcie działa naprawdę.
             */
            const doPatrzacego = Math.cos(((obrot + kat) * Math.PI) / 180);
            const cien = 0.45 * (1 - Math.max(0, doPatrzacego));

            return (
              <div
                key={k.id}
                className="absolute top-0 flex flex-col gap-[3px]"
                style={{
                  width: span * MIEJSCE_PX - SZCZELINA_PX,
                  height: wysokosc,
                  /**
                   * ⚠️ KOLUMNĘ CENTRUJEMY NA OSI PRZED OBROTEM — i to jest poprawka zgłoszonego
                   * błędu „w 3d kolumna wygląda dziwnie, nakładają się na siebie".
                   *
                   * Wcześniej element siedział przy lewej krawędzi rodzica, a nadmiarową szerokość
                   * kolumny poszerzonej odejmował `translateX(-(span-1)·W/2)` PO obrocie. To jest
                   * poprawne wyłącznie przy kącie zero: `translateX` działa w układzie już
                   * obróconym, więc potrzebna poprawka wynosi `-(span-1)·W/(2·cos θ)` i dochodzi
                   * do tego błąd wzdłuż osi Z równy `-dx·sin θ`. Kolumny rozjeżdżały się więc
                   * tym bardziej, im dalej od środka — i zachodziły na siebie przy krawędziach.
                   *
                   * `left: 50%` z ujemnym marginesem stawia ŚRODEK elementu (czyli jego
                   * `transform-origin`) dokładnie na osi walca, więc żadna poprawka nie jest
                   * potrzebna: zostaje czysty obrót i odsunięcie o promień.
                   */
                  left: '50%',
                  marginLeft: -(span * MIEJSCE_PX - SZCZELINA_PX) / 2,
                  transform: `rotateY(${obrot}deg) translateZ(${promien}px)`,
                  /** ⚠️ bez tego `translateZ` na zaznaczonej komórce nie ma w czym się wysunąć */
                  transformStyle: 'preserve-3d',
                  /**
                   * Tył bębna znika — patrzymy na walec, a nie przez niego.
                   *
                   * ⚠️ LICZYMY TO SAMI, ZAMIAST `backface-visibility: hidden`. Warunek jest ten sam
                   * (ściana odwrócona od patrzącego), ale tamta własność promuje kolumnę do własnej
                   * warstwy kompozytora — czyli znów rasteryzacja przed rzutowaniem, ten sam koszt
                   * ostrości co przy filtrze. Przy okazji odwrócone kolumny przestają być malowane.
                   */
                  visibility: doPatrzacego <= 0 ? 'hidden' : 'visible',
                }}
              >
                {/* Numer nad kolumną — jedzie razem z nią po obwodzie, więc zawsze wiadomo, co się ogląda. */}
                <div className="pb-1 text-center text-[11px] font-semibold leading-4 text-foreground">
                  {k.drawerNo}
                </div>
                {wiersze.map((rowNo) => {
                  const c = poWierszach.get(rowNo) ?? null;
                  const kolor = kolorPrzegrodki(c);
                  if (kolor === null || c === null) {
                    // w tej kolumnie NIE MA takiego wiersza — inny stan niż „pusta"
                    return <div key={rowNo} style={{ height: WIERSZ_PX - 2 }} />;
                  }
                  const { bg, ink, obrys } = kolor;
                  const wybrana = zaznaczone.has(c.id);
                  const szukanie = stanSzukania(c, szukany);
                  return (
                    <button
                      key={rowNo}
                      type="button"
                      onClick={(e) => onToggle(c, { shift: e.shiftKey, toggle: e.ctrlKey || e.metaKey })}
                      title={
                        c.item
                          ? `${t('planogram.column')} ${k.drawerNo}, ${t('planogram.row')} ${rowNo}\n${c.item.name}\n${c.qtyCurrent}/${c.qtyMax}` +
                            (c.needsRestock
                              ? `\n${t('planogram.d3.restock')}${c.criticalLevel === null ? '' : ` (${t('planogram.d3.threshold')} ${c.criticalLevel})`}`
                              : '')
                          : `${t('planogram.column')} ${k.drawerNo}, ${t('planogram.row')} ${rowNo}: ${t('planogram.emptySlot')}`
                      }
                      /*
                        ⚠️ RAMKA NA KAŻDEJ KOMÓRCE, także wypełnionej — wcześniej była przezroczysta
                        i zgłoszono, że „przerwy między przegrodami są dość słabo odznaczone".
                        Na bębnie sąsiednie komórki mają często ten sam kolor (cała kolumna jednym
                        towarem), więc bez krawędzi zlewają się w jeden słupek i nie da się policzyć,
                        ile ich jest.
                      */
                      /*
                        ⚠️ ZAZNACZONA PRZEGRÓDKA WYSUWA SIĘ Z BĘBNA. Sama obwódka ginęła:
                        komórki są małe, a kolumny przyciemnione cieniowaniem od kąta, więc
                        `ring` na ciemnym boku był nie do zauważenia. Wysunięcie o kilkanaście
                        pikseli wzdłuż promienia widać z każdej strony i z każdej jasności —
                        i to jest dokładnie to, o co poproszono: „zaznaczać i powiększać".
                      */
                      className={cn(
                        'w-full rounded-[2px] border text-[8px] leading-none transition-transform',
                        obrys !== null ? 'border-dashed' : 'border-solid',
                        wybrana && 'ring-2 ring-white',
                        /*
                          ⚠️ SZUKANIE OSOBNYM ZNAKIEM OD ZAZNACZENIA — obwódka biała znaczy tu
                          „wybrane do zmiany", więc znalezione dostaje kolor własny, a reszta
                          gaśnie. Na walcu przygaszenie działa nawet lepiej niż w siatce: ciemna
                          strona bębna i tak jest przyciemniona, a pojedyncze jasne komórki
                          widać przez pół obrotu.
                        */
                        szukanie === 'obok' && 'opacity-20',
                        szukanie === 'trafienie' && 'ring-2 ring-[var(--pg-znaleziony)]',
                      )}
                      style={{
                        height: WIERSZ_PX - 3,
                        background: bg,
                        color: ink,
                        borderColor: obrys ?? 'rgba(0,0,0,0.35)',
                        ...(wybrana
                          ? {
                              transform: `translateZ(${WYSUNIECIE_PX}px) scale(1.08)`,
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 6px 14px rgba(0,0,0,0.45)',
                              zIndex: 1,
                            }
                          : {}),
                      }}
                    >
                      {etykiety && c.item !== null && (
                        /**
                         * ⚠️ 8 px, NIE 7. Po zdjęciu filtru napis jest rysowany ostro, więc można
                         * mu dać wielkość, przy której litery mają szansę się różnić — przy 7 px
                         * „B" i „8" wyglądają tak samo na każdym ekranie, choćby były idealnie
                         * wyrenderowane. Przednia ściana jest dodatkowo powiększana 1,67×, więc
                         * na wprost czyta się to jak 13 px.
                         */
                        <span className="block truncate px-px text-[8px] leading-[11px]">{c.item.name}</span>
                      )}
                    </button>
                  );
                })}

                {/*
                  CIEŃ KOLUMNY — półprzezroczysta warstwa NA WIERZCHU, zamiast filtru na całości.
                  Uzasadnienie przy `cien`. `pointer-events-none`, bo pod spodem są przyciski
                  przegródek, a `aria-hidden`, bo dla czytnika ekranu to nie jest żadna treść.

                  ⚠️ ZAZNACZONA PRZEGRÓDKA ZOSTAJE JASNA I TO JEST ZYSK, NIE NIEDOPATRZENIE:
                  wysuwa się wzdłuż promienia (`translateZ`), więc w przestrzeni 3D leży PRZED tą
                  warstwą i cień jej nie dosięga. Wybrana komórka na ciemnym boku bębna świeci
                  teraz sama z siebie.
                */}
                {cien > 0.001 && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{ background: `rgba(0,0,0,${cien.toFixed(3)})` }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {/* Pod ramką ZOSTAJE SAMA LEGENDA — sterowanie przeniosło się do środka kadru. */}
        {/*
          ⚠️ TA SAMA LEGENDA CO W SIATCE, bo i paleta jest ta sama. Osobna legenda z trzema
          własnymi nazwami była właśnie tym, przez co przegródka pusta wychodziła w bębnie
          żółta, a w siatce czerwona.
        */}
        {STOCK_STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <span key={s.key} className="flex items-center gap-1.5">
              <span
                className="inline-flex size-4 items-center justify-center rounded-[2px]"
                style={{ background: s.bg, color: s.ink }}
              >
                <Icon className="size-2.5" />
              </span>
              {t(`planogram.${s.key}`)}
            </span>
          );
        })}
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block size-3 rounded-[2px] border border-dashed"
            style={{ borderColor: BEZ_TOWARU.border }}
          />
          {t('planogram.emptySlot')}
        </span>
      </div>
    </div>
  );
}
